<template>
  <div class="relative">
    <button
      data-test="notification-bell"
      class="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors active:scale-90 relative"
      @click="toggleDropdown"
    >
      <PhBell :size="24" weight="bold" />
      <div
        v-if="store.unreadCount > 0"
        data-test="unread-count"
        class="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
      >
        {{ store.unreadCount > 99 ? '99+' : store.unreadCount }}
      </div>
    </button>

    <!-- Dropdown -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="isOpen"
        v-click-outside="closeDropdown"
        class="absolute left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
      >
        <div class="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 class="font-bold text-gray-800">{{ t('notifications.title') }}</h3>
          <button
            v-if="store.unreadCount > 0"
            class="text-xs text-blue-600 font-semibold hover:text-blue-800"
            @click="store.markAllAsRead"
          >
            {{ t('notifications.read_all') }}
          </button>
        </div>

        <div class="max-h-[400px] overflow-y-auto" @scroll="handleScroll">
          <div v-if="store.notifications.length === 0 && !store.loading" class="p-8 text-center text-gray-500">
            <PhBellSlash :size="32" class="mx-auto mb-2 opacity-20" />
            <p class="text-sm">{{ t('notifications.empty') }}</p>
          </div>

          <div
            v-for="notif in store.notifications"
            :key="notif.id"
            data-test="notification-item"
            class="p-4 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors relative"
            :class="{ 'bg-blue-50/30': !notif.read_at }"
            @click="handleNotificationClick(notif)"
          >
            <div v-if="!notif.read_at" class="absolute top-4 right-2 w-2 h-2 bg-blue-500 rounded-full" />
            <div class="pr-2">
              <h4 class="text-sm font-bold text-gray-800 mb-0.5">
                {{ t(`notifications.${notif.type}.title`, notif.parameters) }}
              </h4>
              <p class="text-xs text-gray-600 line-clamp-2 mb-1">
                {{ t(`notifications.${notif.type}.body`, notif.parameters) }}
              </p>
              <span class="text-[10px] text-gray-400">{{ formatTime(notif.created_at) }}</span>
            </div>
          </div>

          <div v-if="store.loading" class="p-4 text-center">
            <div class="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { PhBell, PhBellSlash } from '@phosphor-icons/vue'
import { useNotificationStore } from '~/stores/notifications'
import { useI18n } from '#i18n'
import type { NotificationDto } from '~/types'
import moment from 'jalali-moment'

const { t } = useI18n()
const { $i18n } = useNuxtApp()
const store = useNotificationStore()
const isOpen = ref(false)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    store.fetchNotifications(true)
  }
}

const closeDropdown = () => {
  isOpen.value = false
}

const handleNotificationClick = (notif: NotificationDto) => {
  if (!notif.read_at) {
    store.markAsRead(notif.id)
  }
  if (notif.link) {
    navigateTo(notif.link)
    closeDropdown()
  }
}

const handleScroll = (e: Event) => {
  const target = e.target as HTMLElement
  if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
    store.fetchNotifications()
  }
}

const formatTime = (dateStr: string) => {
  const m = moment.utc(dateStr).local()
  if ($i18n.locale.value === 'fa') {
    return m.locale('fa').format('jYYYY/jMM/jDD HH:mm')
  }
  return m.locale('en').format('YYYY/MM/DD HH:mm')
}

onMounted(() => {
  store.fetchUnreadCount()
  // Refresh unread count every minute
  setInterval(() => {
    store.fetchUnreadCount()
  }, 60000)
})
</script>
